import React, { useState } from 'react';
import { Beer, Loader2 } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useLocationScope } from '../context/LocationContext';
import { canCheckInAtBar } from '../data/checkInPhrases';
import { CHECK_IN_MAX_FEET, getDistanceFeet } from '../utils/geo';

function NearbyCheckInButton({ variant = 'icon', className = '' }) {
  const { user } = useAuth();
  const { bars, getUserRating, addCheckIn } = useApp();
  const { userLocation, requestLocation, locationLoading } = useLocationScope();
  const [status, setStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!user) return null;

  const handleCheckIn = async (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    setStatus('');

    let coords = userLocation;
    if (!coords) {
      requestLocation();
      setStatus('Turn on location to check in.');
      return;
    }

    const nearby = bars
      .map((bar) => ({
        bar,
        feet: getDistanceFeet(coords.lat, coords.lng, bar.lat, bar.lng),
        rated: canCheckInAtBar(getUserRating(bar.id)),
      }))
      .filter((item) => item.feet <= CHECK_IN_MAX_FEET)
      .sort((a, b) => a.feet - b.feet);

    const ratedNearby = nearby.filter((item) => item.rated);
    if (ratedNearby.length === 0) {
      if (nearby.length > 0) {
        setStatus(`You're at ${nearby[0].bar.name}. Rate it once to check in.`);
      } else {
        setStatus(`Get within ${CHECK_IN_MAX_FEET} feet of a bar you've rated.`);
      }
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await addCheckIn(ratedNearby[0].bar.id);
      setStatus(result?.message_text || `Checked in at ${ratedNearby[0].bar.name}.`);
    } catch (err) {
      setStatus(err.message || 'Could not check in right now.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const busy = isSubmitting || locationLoading;
  const circle = variant === 'circle';
  const full = variant === 'full';

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={handleCheckIn}
        disabled={busy}
        title="Check in at a nearby bar"
        className={
          circle
            ? 'glass rounded-full p-3 text-dive-amber hover:bg-amber-500/20 transition-all'
            : full
              ? 'w-full flex items-center justify-center gap-2 px-3 py-2 bg-dive-amber/20 border border-dive-amber text-dive-amber rounded-lg hover:bg-dive-amber/30'
              : 'p-3 rounded-xl bg-dive-dark text-dive-amber hover:text-amber-300 transition-all'
        }
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Beer className="w-5 h-5" />}
        {full && <span className="text-sm font-medium">{isSubmitting ? 'Checking in...' : "I'm here"}</span>}
      </button>
      {status && (
        <p className="absolute top-full right-0 mt-2 z-[1100] text-xs text-dive-amber bg-dive-dark/95 border border-dive-gray rounded-lg px-2 py-1 max-w-[200px] pointer-events-none">
          {status}
        </p>
      )}
    </div>
  );
}

export default NearbyCheckInButton;
